import { Component } from 'preact';
import * as THREE from 'three';

// GPU ember field for the victory result card. One Points draw call with a
// custom shader: every particle carries its own seed, depth layer, size and
// life offset; the vertex shader integrates rise + noise-driven drift from
// time alone (no per-frame CPU work), the fragment shader draws a soft bokeh
// disc whose edge softness follows the depth layer (near = big + blurry,
// far = small + sharp) and whose colour cools over life (white → gold →
// orange → dull red) like a real ember.

const COUNT = 600;

// Physical model, per the standard real-time ember recipe:
//   • advection through a DIVERGENCE-FREE curl-noise field (Bridson 2007) so
//     the flow swirls like air instead of sliding along a sine
//   • buoyancy proportional to the ember's temperature, which decays
//     exponentially. It lifts hard while hot, then loses its ride and the
//     air alone carries it. Some cinders sink
//   • quadratic drag, so velocity approaches the local air velocity rather
//     than growing without bound
//   • tumble: a non-spherical cinder spins, so its silhouette and the light
//     it throws flash as it turns
// The trajectory is integrated in the vertex shader (16 Euler steps). A
// closed-form path would repeat identically every cycle, which is exactly
// what reads as synthetic.
const VERT = /* glsl */`
  uniform float uTime;
  uniform vec2 uSize;
  uniform float uDpr;
  attribute float aSeed;
  attribute float aLayer;   // 0 far/small .. 1 near/big
  attribute float aOffset;
  attribute float aSpeed;
  varying float vLife;
  varying float vLayer;
  varying float vSeed;
  varying float vStretch;
  varying float vAngle;
  varying float vTemp;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec3 p) {
    vec3 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n = i.x + i.y * 57.0 + i.z * 113.0;
    float a = mix(mix(fract(sin(n) * 43758.5), fract(sin(n + 1.0) * 43758.5), f.x),
                  mix(fract(sin(n + 57.0) * 43758.5), fract(sin(n + 58.0) * 43758.5), f.x), f.y);
    float b = mix(mix(fract(sin(n + 113.0) * 43758.5), fract(sin(n + 114.0) * 43758.5), f.x),
                  mix(fract(sin(n + 170.0) * 43758.5), fract(sin(n + 171.0) * 43758.5), f.x), f.y);
    return mix(a, b, f.z);
  }
  // Two octaves is enough: the big one sets the plume's wander, the small
  // one the local chop.
  float fbm(vec3 p) { return noise(p) * 0.65 + noise(p * 2.7 + 11.3) * 0.35; }

  // Curl of a 2D scalar potential = a divergence-free 2D flow. Air does not
  // pile up or vanish, so neither do the embers riding it.
  vec2 curl(vec2 p, float t) {
    const float e = 0.06;
    float n1 = fbm(vec3(p.x, p.y + e, t));
    float n2 = fbm(vec3(p.x, p.y - e, t));
    float n3 = fbm(vec3(p.x + e, p.y, t));
    float n4 = fbm(vec3(p.x - e, p.y, t));
    return vec2(n1 - n2, n4 - n3) / (2.0 * e);
  }

  void main() {
    float ttl = mix(15.0, 9.0, aLayer) * (0.75 + 0.5 * hash(vec2(aSeed, 3.7)));
    float raw = uTime * aSpeed + aOffset * ttl;
    float t = mod(raw, ttl);
    // A fresh seed every rebirth: without this each ember retraces one
    // memorised path forever, which is the single biggest tell.
    float cycle = floor(raw / ttl);
    float seed = fract(aSeed + cycle * 0.61803399);
    float life = t / ttl;
    vLife = life; vLayer = aLayer; vSeed = seed;

    // Emitted from the bottom edge with a small random upward kick.
    vec2 p = vec2(hash(vec2(seed, 1.0)), -0.05);
    vec2 v = vec2((hash(vec2(seed, 9.0)) - 0.5) * 0.08, 0.14 + 0.12 * hash(vec2(seed, 4.0)));

    // Heavier cinders carry more mass per area: they lift less and drag less.
    float mass = mix(0.55, 1.5, aLayer);
    float cool = 0.55 + 0.6 * hash(vec2(seed, 6.0));  // per-ember cooling rate
    float drag = mix(2.6, 1.4, aLayer);
    float turb = mix(0.18, 0.34, aLayer);

    const int STEPS = 16;
    float dt = t / float(STEPS);
    float temp = 1.0;
    for (int i = 0; i < STEPS; i++) {
      float ti = dt * float(i);
      temp = exp(-cool * ti / ttl * 3.0);
      // Air velocity: the plume rises and swirls; the swirl is animated so
      // the field itself is never the same twice.
      // Two scales of air. The curl term is the local swirl right off the
      // fire; the gust term is a big lazy wind that wanders across the whole
      // card over ~10s and shoves whole handfuls of embers sideways.
      vec2 air = curl(p * 2.2 + vec2(seed * 30.0, 0.0), ti * 0.35 + uTime * 0.08) * turb;
      vec2 gust = curl(p * 0.55, uTime * 0.03 + 40.0) * 0.16;
      air += gust;
      // The updraft column is strongest over the fire and dies off with
      // height, so embers stop climbing and start being carried.
      air.y += 0.16 * (1.0 - smoothstep(-0.1, 1.35, p.y));
      vec2 buoy = vec2(0.0, 0.62 * temp / mass);       // lift dies with heat
      vec2 rel = air - v;
      vec2 dragF = rel * length(rel) * drag;           // quadratic drag
      vec2 acc = buoy + dragF + vec2(0.0, -0.16 / mass); // gravity
      v += acc * dt;
      p += v * dt;
    }
    vTemp = temp;

    p.x = fract(p.x);
    vec3 pos = vec3((p.x - 0.5) * uSize.x, (p.y - 0.5) * uSize.y, 0.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

    float px = mix(2.8, 14.0, aLayer * aLayer) * (0.7 + 0.7 * hash(vec2(seed, 5.0)));
    px *= mix(1.0, 0.72, life);
    gl_PointSize = px * uDpr;

    // Streak along the direction of travel, scaled by speed.
    float sp = length(v);
    vStretch = 1.0 + clamp(sp * 2.2, 0.0, 1.9);
    vAngle = atan(v.y, v.x) - 1.5707963;
    // Tumble rate falls with size; the flash it produces is in the fragment.
    vAngle += sin(t * (7.0 + hash(vec2(seed, 8.0)) * 9.0)) * 0.5;
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  varying float vLife;
  varying float vLayer;
  varying float vSeed;
  varying float vStretch;
  varying float vAngle;
  varying float vTemp;

  void main() {
    // Rotate the sample space into the ember's travel direction, then
    // squash across it. The sprite elongates along its own velocity.
    vec2 uv = gl_PointCoord - 0.5;
    float c = cos(vAngle), s = sin(vAngle);
    uv = mat2(c, -s, s, c) * uv;
    uv.y /= vStretch;
    float d = length(uv) * 2.0;

    float soft = mix(0.5, 0.95, vLayer);
    float halo = 1.0 - smoothstep(1.0 - soft, 1.0, d);
    float core = 1.0 - smoothstep(0.0, 0.28, d);

    float env = smoothstep(0.0, 0.04, vLife) * (1.0 - smoothstep(0.72, 1.0, vLife));
    // A tumbling cinder shows its broad face intermittently. That is the
    // flash, not a sine on brightness.
    float face = abs(sin(vLife * 47.0 + vSeed * 30.0));
    float flick = mix(0.62, 1.0, face * face);

    // Blackbody-ish ramp driven by TEMPERATURE, not by elapsed life, so an
    // ember caught in a fast updraft stays hot longer than one that stalls.
    vec3 red   = vec3(0.5, 0.06, 0.01);
    vec3 orange= vec3(1.0, 0.36, 0.05);
    vec3 gold  = vec3(1.0, 0.72, 0.24);
    vec3 white = vec3(1.0, 0.95, 0.8);
    float T = clamp(vTemp, 0.0, 1.0);
    vec3 col = T < 0.35 ? mix(red, orange, T / 0.35)
             : T < 0.7  ? mix(orange, gold, (T - 0.35) / 0.35)
             : mix(gold, white, (T - 0.7) / 0.3);

    float alpha = (halo * 0.7 + core * 1.5) * env * flick * mix(0.55, 1.0, T);
    alpha *= mix(1.0, 0.62, vLayer);
    gl_FragColor = vec4(col * alpha, alpha);
  }
`;

export default class VictoryEmbers extends Component {
  componentDidMount() {
    const canvas = this.canvas;
    if (!canvas) return;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, premultipliedAlpha: true, powerPreference: 'low-power' });
    } catch { return; }
    this.renderer = renderer;
    renderer.setClearColor(0x000000, 0);
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10);
    this.camera.position.z = 1;

    const seed = new Float32Array(COUNT), layer = new Float32Array(COUNT), offset = new Float32Array(COUNT), speed = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      seed[i] = Math.random();
      layer[i] = Math.pow(Math.random(), 1.8);    // mostly small cinders, a few big near-field ones
      offset[i] = Math.random();
      speed[i] = 0.45 + Math.random() * 0.35;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(COUNT * 3), 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    geo.setAttribute('aLayer', new THREE.BufferAttribute(layer, 1));
    geo.setAttribute('aOffset', new THREE.BufferAttribute(offset, 1));
    geo.setAttribute('aSpeed', new THREE.BufferAttribute(speed, 1));
    this.uniforms = { uTime: { value: 0 }, uSize: { value: new THREE.Vector2(2, 2) }, uDpr: { value: 1 } };
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms, vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(geo, mat);
    this.scene.add(this.points);

    this.resize();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(canvas);
    this.start = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    if (this.points) { this.points.geometry.dispose(); this.points.material.dispose(); }
    if (this.renderer) { this.renderer.dispose(); this.renderer.forceContextLoss(); }
  }

  resize() {
    const c = this.canvas;
    const w = c.clientWidth || 1, h = c.clientHeight || 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    const aspect = w / h;
    this.camera.left = -aspect; this.camera.right = aspect;
    this.camera.updateProjectionMatrix();
    this.uniforms.uSize.value.set(aspect * 2, 2);
    this.uniforms.uDpr.value = dpr;
  }

  tick = (now) => {
    this.uniforms.uTime.value = (now - this.start) / 1000;
    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.tick);
  };

  render() {
    return (
      <div className="absolute inset-0 pointer-events-none match-result-particles">
        {/* The fire the embers come off. Anchors the upward flow so it reads
            as a hearth below the frame rather than free-floating sparks. */}
        <div className="match-result-hearth absolute inset-x-0 bottom-0" />
        <canvas ref={(el) => { this.canvas = el; }} className="absolute inset-0 w-full h-full" aria-hidden />
      </div>
    );
  }
}
